import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import {
	selectCurrentCompany,
	selectCurrentCompanyId,
	selectCurrentProfileType,
	selectCurrentUserId,
	selectUser,
} from "newui/features/Auth/authSelectors";
import { setUserRoles } from "newui/features/Auth/authSlice";
import type { User } from "newui/features/Auth/types/User.type";
import type { UserCompanyProfile } from "newui/features/Auth/types/UserCompanyProfile.type";
import type { UserRoleName } from "newui/features/Auth/types/UserRole.type";
import type { RootState } from "newui/store";
import { useAppSelector } from "newui/store/hooks";
import { useEffect, useMemo } from "react";
import { CompanyProfileType } from "newui/features/Auth/types/CompanyProfileType";

interface CurrentUser {
	currentUser: User | null;
	guestUserEmail: string | null;
	isCompanyAdmin: boolean;
	isExecutiveUser: boolean;
	isGuest: boolean;
	isSuperAdmin: boolean;
}

export const useCurrentCompany = (): UserCompanyProfile | null => {
	const currentCompany = useAppSelector(selectCurrentCompany);
	return currentCompany as UserCompanyProfile | null;
};

export const useCurrentCompanyId = (): number | null => {
	const currentCompanyId = useAppSelector(selectCurrentCompanyId);
	return currentCompanyId;
};

export const useCurrentProfileType = ():
	CompanyProfileType => {
	return useAppSelector(selectCurrentProfileType);
};

/**
 * Allows you to get any specific value from the current company profile
 * Will ensure only re-renders when that value changes
 * @param key the key from UserCompanyProfile to get
 * @returns key value type
 */
export const useCurrentCompanyValue = <K extends keyof UserCompanyProfile>(
	key: K
): UserCompanyProfile[K] | null => {
	return useAppSelector(
		(state: RootState) => state.auth.currentCompany?.[key] ?? null
	);
};

export const useCurrentUser = (): CurrentUser | null => {
	const currentUser = useAppSelector(selectUser);
	return useMemo(() => {
		if (!currentUser) {
			return {
				currentUser: null,
				guestUserEmail: null,
				isGuest: false,
				isCompanyAdmin: false,
				isSuperAdmin: false,
				isExecutiveUser: false,
			} as CurrentUser | null;
		}

		return {
			currentUser,
			guestUserEmail: currentUser.is_guest ? currentUser.email : '',
			isGuest: currentUser.is_guest,
			isCompanyAdmin: currentUser.company_admin,
			isSuperAdmin: currentUser.is_admin,
			isExecutiveUser: currentUser.is_executive,
		};
	}, [currentUser]) as CurrentUser | null;
};

/**
 * Whether the current user may reach the company-administration surfaces —
 * User Management and Roles & Permissions.
 *
 * Keys off the RBAC v2 `administers_company_rbac` signal, which mirrors the
 * backend's `User::administersCompanyRbac()` exactly: System Administrator
 * (procurer or bidder side) plus the retained platform-admin / authorised-
 * executive superusers.
 *
 * This previously read the LEGACY `company_admin` boolean, which no RBAC-seeded
 * user carries — so even a Procurer System Administrator with `settings = Full`
 * hit a client-side 404 on the roles page, while a legacy company-admin with no
 * RBAC role could open it. Do NOT swap this back to `isCompanyAdmin`: that flag
 * is still used elsewhere (supplier performance, corporate documents) for
 * unrelated checks and does not track RBAC standing.
 */
export const useIsCurrentUserAdmin = (): boolean => {
	const { currentUser } = useCurrentUser() || {};
	return !!currentUser?.administers_company_rbac;
}

/**
 * PRO-737 — this account holds no RBAC v2 standing, so it gets the
 * limited-access state instead of the app.
 *
 * Fails CLOSED on the notice, not on access: an absent flag reads as false, so
 * an older payload renders the app as before rather than locking a user out of
 * a client the server would still serve. The lockout itself lives on the server
 * (EnforceRolelessRestriction); this only decides what to render.
 */
export const useHasNoRbacAccess = (): boolean => {
	const { currentUser } = useCurrentUser() || {};
	return currentUser?.has_no_rbac_access === true;
};

export const useCurrentUserId = (): number | null => {
	const currentUserId = useAppSelector(selectCurrentUserId);
	return currentUserId;
};

export const useCurrentUserHasRole = (roleName: UserRoleName): boolean => {
	return useCurrentUserRoles().some((role) => role === roleName);
};

/**
 * The current user's LEGACY (Spatie) role names, mirrored into the store.
 *
 * PRO-741: this used to dispatch DURING RENDER, guarded by "the store copy is
 * empty". An empty array is truthy, so a user whose payload carried `roles: []`
 * never took the early return: it dispatched a fresh `[]`, the store copy was
 * still empty, and the next render dispatched again — React error #185,
 * "Maximum update depth exceeded", which ticks up until the tab dies. Nobody
 * saw it while every account carried a legacy role; RBAC v2 assignments are not
 * Spatie roles, so a user holding only scoped RBAC standing arrives here with
 * an empty array and the guard closes on nothing. The one consumer sits on the
 * RFx create/edit header, which is why only that page crashed.
 *
 * Two changes keep it terminating:
 *
 *   - the mirror moved into an effect, where a dispatch belongs, and fires only
 *     when the store copy actually DIFFERS from the payload. Equality is the
 *     termination condition; "non-empty" never was one.
 *   - the derived value is returned directly rather than the store copy, so the
 *     first render already answers correctly instead of reporting "no roles"
 *     until a second pass lands. This hook is the only writer of `user_roles`,
 *     so the two can never disagree about anything but timing.
 */
export const useCurrentUserRoles = (): UserRoleName[] => {
	const existingRoles = useSelector(
		(state: RootState) => state?.auth?.user_roles
	);
	const { currentUser } = useCurrentUser();
	const dispatch = useDispatch();

	const roles = currentUser?.roles;

	const derivedRoles = useMemo(
		() => (roles ?? []).map((role) => role.name as UserRoleName),
		[roles]
	);

	const isMirrored =
		!!existingRoles &&
		existingRoles.length === derivedRoles.length &&
		derivedRoles.every((role, index) => existingRoles[index] === role);

	useEffect(() => {
		if (isMirrored) {
			return;
		}

		dispatch(setUserRoles(derivedRoles));
	}, [dispatch, derivedRoles, isMirrored]);

	return derivedRoles;
};
